import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Transition } from 'react-transition-group';
import anime from 'animejs/lib/anime.es.js';
import Button from 'components/button/index';
import Portal from './portal';
import { Context, lockBodyScroll, unlockBodyScroll } from './context';
import * as css from './style';

/* TODO: Unmount props.children when drawer is closed (once animation is finished). Should improve the JS Heap Performance (gargabe collection) */
/* Using CSSTransitions component performed worse than using CSS animation via styled-components and props */

const animateIn = (node) => anime({
  targets: node,
  translateX: () => ['110%', '0%'],
  duration: 600,
  easing: 'easeInOutQuart',
});

const animateOut = (node) => anime({
  targets: node,
  translateX: () => ['0%', '100%'],
  duration: 600,
  easing: 'easeInOutQuart',
});

const animateFadeIn = (node) => anime({
  targets: node,
  duration: 1,
  easing: 'linear',
  translateY: 0,
});

const animateFadeOut = (node) => anime({
  targets: node,
  duration: 1,
  easing: 'linear',
  translateY: 0,
});

/**
 * How many mounted DrawerMounts currently render each drawer id. The provider
 * outlives the page (it wraps every route), so a drawer that unmounts while
 * open — navigating away, switching a tab panel — would otherwise stay in
 * `show` forever and keep the body scroll locked (PS-7527). A drawer is only
 * closed once no instance renders its id any more, so parents that re-key a
 * Drawer to reset it (uploader, media panels) don't close it on the way.
 */
const mountedDrawers = new Map<string, number>();

const DrawerMount = ({ size, context, children, title, id, hasTabs, subTitle, showAsModal=false, showHeader=true, showButtonToggle=true, showOverlay=false }) => {
  const isOpen = context.show && context.show.indexOf(id) !== -1;
  const nodeRef = useRef(null);
  const overlayRef = useRef(null);
  const closeDrawerRef = useRef(context.closeDrawer);
  closeDrawerRef.current = context.closeDrawer;

  /**
   * Close the drawer in context when its last instance unmounts. Layout effect
   * so the count is updated synchronously inside the commit; the close check
   * is deferred a microtask so a replacement instance mounted in the same
   * commit has registered first.
   */
  useLayoutEffect(() => {
    mountedDrawers.set(id, (mountedDrawers.get(id) || 0) + 1);

    return () => {
      const remaining = (mountedDrawers.get(id) || 1) - 1;
      mountedDrawers.set(id, remaining);

      Promise.resolve().then(() => {
        if ((mountedDrawers.get(id) || 0) === 0) closeDrawerRef.current(id);
      });
    };
  }, [id]);

  /**
   * show blur effect in background and lock the page scroll when model is open in center.
   * Side drawers leave the page scrollable so long editors behind them can still be
   * worked with (CMS-7859).
   */
  useEffect(() => {
    if (!(isOpen && showAsModal)) return undefined;

    const root = document.getElementById('root');
    root.style.filter = 'blur(2px)';
    lockBodyScroll();
    // Cleanup also runs on unmount, so the blur and lock can't outlive the modal
    return () => {
      root.style.filter = 'none';
      unlockBodyScroll();
    };
  }, [isOpen, showAsModal]);

  return (
    <>
    <Transition
      in={isOpen}
      nodeRef={nodeRef}
      timeout={600}
      onEnter={(_node) => { showAsModal ? animateFadeIn(nodeRef.current) : animateIn(nodeRef.current) }}
      onExit={(_node) => { showAsModal ? animateFadeOut(nodeRef.current) : animateOut(nodeRef.current) }}
      unmountOnExit
      appear
    >
      <css.Container ref={nodeRef} size={size} showAsModal={showAsModal} show={isOpen} className="drawer-container" showOverlay={showOverlay}>
        {
          showHeader &&
          <css.Header>
            <span className="drawer-title">{title}</span>
            { subTitle && <css.subTitle>{subTitle}</css.subTitle>}
            { showButtonToggle && <Button icon="arrow-circle-right" className="button-close" onClick={context.toggleShow} /> }
          </css.Header>
        }

        <css.Body hasTabs={hasTabs} showAsModal={showAsModal}>
          {children}
        </css.Body>
      </css.Container>
    </Transition>
    {
      showOverlay && (
        <Transition
          in={isOpen}
          nodeRef={overlayRef}
          timeout={600}
          onEnter={(_node) => { showAsModal ? animateFadeIn(overlayRef.current) : animateIn(overlayRef.current) }}
          onExit={(_node) => { showAsModal ? animateFadeOut(overlayRef.current) : animateOut(overlayRef.current) }}
          unmountOnExit
          appear
        >
          <css.Overlay ref={overlayRef} />
        </Transition>
      )
    }
    </>
  );
};

interface Props {
  children: React.ReactNode,
  title: string,
  id: string,
  size?: 'default' | 'wide' | 'medium' | 'large' | 'largest',
  hasTabs?: boolean,
  subTitle?: string,
  showAsModal?: boolean,
  showHeader?: boolean,
  showButtonToggle?: boolean,
  showOverlay?: boolean,
}

const Drawer = (props: Props) => (
  <Portal>
    <Context.Consumer>
      {(context) => (
        <DrawerMount
          size={props.size}
          context={context}
          title={props.title}
          id={props.id}
          hasTabs={props.hasTabs}
          subTitle={props.subTitle}
          showAsModal={props.showAsModal}
          showHeader={props.showHeader}
          showButtonToggle={props.showButtonToggle}
          showOverlay={props.showOverlay}
        >
          {props.children}
        </DrawerMount>
      )}
    </Context.Consumer>
  </Portal>
);

Drawer.defaultProps = {
  size: 'default',
  hasTabs: false,
  subTitle: "",
};

export default Drawer;
